import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { JsonLd } from "@/components/JsonLd";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useSiteAudit } from "@/components/audit/useSiteAudit";
import { AuditCheckList } from "@/components/audit/AuditCheckList";
import { breadcrumbSchema } from "@/lib/seo";
import { bnPageHead } from "@/lib/bn-head";
import { SITE } from "@/content/site";

const CRUMBS = [
  { name: "হোম", path: "/bn" },
  { name: "ফ্রি এসইও অডিট", path: "/bn/free-seo-audit" },
];

export const Route = createFileRoute("/bn/free-seo-audit")({
  head: () =>
    bnPageHead({
      path: "/bn/free-seo-audit",
      enPath: "/free-seo-audit",
      title: "ফ্রি এসইও অডিট টুল — ওয়েবসাইটের এসইও চেক করুন",
      desc: "যেকোনো ওয়েবসাইটের ফ্রি ও তাৎক্ষণিক এসইও অডিট — টাইটেল, মেটা ডেসক্রিপশন, হেডিং, ইনডেক্সযোগ্যতা, স্কিমা ও গতি। কোনো সাইন-আপ লাগবে না।",
      keywords: ["free seo audit bangla", "ফ্রি এসইও অডিট", "ওয়েবসাইট এসইও চেকার", "seo checker bangladesh"],
    }),
  component: BnAuditTool,
});

const STATUS_BN: Record<string, string> = { pass: "পাস", warn: "সতর্কতা", info: "তথ্য", fail: "সমস্যা" };

function BnAuditTool() {
  const { url, setUrl, loading, result, error, onSubmit } = useSiteAudit({
    invalid: () => "সঠিক একটি পাবলিক ওয়েবসাইট ঠিকানা দিন, যেমন yourdomain.com",
    serverError: (serverError) => `অডিট করা যায়নি: ${serverError}`,
    failed: "অডিট শেষ করা যায়নি। একটু পরে আবার চেষ্টা করুন।",
  });

  return (
    <>
      <Breadcrumbs items={CRUMBS} />
      <Section headingLevel={1} eyebrow="ফ্রি এসইও অডিট টুল" title={<>তাৎক্ষণিক <span className="text-gradient">এসইও স্ন্যাপশট।</span></>}>
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8">
          নিচে যেকোনো পাবলিক ওয়েবসাইটের ঠিকানা দিন। টুলটি পেজটি সরাসরি লোড করে যা দেখতে পায় শুধু
          সেটাই জানায় — স্ট্যাটাস ও রিডাইরেক্ট, টাইটেল ও মেটা, হেডিং, স্কিমা, ছবির alt, robots ও
          sitemap, লোড হওয়ার সময় ও পেজের আকার। পূর্ণাঙ্গ ২০০-পয়েন্ট অডিট চান?{" "}
          <Link to="/bn/contact" className="text-foreground underline underline-offset-4">পেইড অডিট বুক করুন।</Link>
        </p>

        <div className="mt-8 rounded-3xl border border-primary/40 bg-primary/5 p-6 lg:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 max-w-3xl">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-primary">পূর্ণ অডিট টুল</div>
            <div className="font-display text-xl mt-1">tool.freelancertamal.com-এ সম্পূর্ণ ফ্রি অডিট চালান</div>
            <div className="text-sm text-muted-foreground mt-1">আরও গভীর ক্রল, Core Web Vitals, অন-পেজ ও টেকনিক্যাল চেক — ফ্রি, সাইন-আপ ছাড়াই।</div>
          </div>
          <a href="https://tool.freelancertamal.com/" target="_blank" rel="noopener" className="inline-flex flex-none items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium">
            ফ্রি টুল খুলুন <ArrowUpRight size={14} />
          </a>
        </div>

        <form onSubmit={onSubmit} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-2xl">
          <label htmlFor="audit-url-bn" className="sr-only">যে ওয়েবসাইট অডিট করবেন তার ঠিকানা</label>
          <input
            id="audit-url-bn"
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="yourdomain.com"
            className="flex-1 min-w-0 rounded-full border border-border bg-card px-6 py-4 text-base md:text-sm focus:outline-none focus:border-primary/60"
          />
          <button type="submit" disabled={loading} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium disabled:opacity-60">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> পেজ লোড হচ্ছে</> : <>অডিট চালান <ArrowUpRight size={16} /></>}
          </button>
        </form>

        {error && <p role="alert" className="mt-4 text-sm text-destructive max-w-2xl">{error}</p>}

        {result && (
          <Reveal>
            <div className="mt-12 rounded-3xl border border-border bg-card p-6 lg:p-12 break-words">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">লাইভ অডিট</div>
                  <div className="font-display text-2xl mt-1 break-all">{result.finalUrl}</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    HTTP {result.statusCode} · {result.ms} ms · {(result.bytes / 1024).toFixed(0)} KB HTML
                  </div>
                </div>
                <div className="md:text-right">
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">পাস করা চেক</div>
                  <div className="font-display text-5xl text-gradient">{result.passed}/{result.total}</div>
                  <div className="text-sm text-muted-foreground">স্কোর {result.score}%</div>
                </div>
              </div>
              <p className="mt-6 text-xs text-muted-foreground">প্রতিটি চেকের বিস্তারিত ফলাফল ইংরেজিতে দেখানো হয়েছে।</p>
              <AuditCheckList
                checks={result.checks}
                className="mt-4 divide-y divide-border"
                statusLabel={(status) => STATUS_BN[status] ?? status}
                warnAsFail
              />
              <p className="mt-8 text-xs text-muted-foreground">
                ওপরের সবকিছু ঠিকানাটির একবারের লাইভ লোড থেকে নেওয়া — অর্থাৎ ক্রলার কাঁচা HTML-এ যা দেখে।
                এটি JavaScript চালায় না, ভেতরের লিংক ক্রল করে না, আর আসল Core Web Vitals মাপে না।
              </p>
              <div className="mt-6 rounded-2xl border border-primary/30 bg-primary/5 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <div className="font-display text-xl">পূর্ণাঙ্গ অডিট চান?</div>
                  <div className="text-sm text-muted-foreground mt-1">২০০টি চেক, লগ ফাইল বিশ্লেষণ ও অগ্রাধিকার অনুযায়ী সমাধানের তালিকা।</div>
                </div>
                <Link to="/bn/contact" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium">
                  পেইড অডিট বুক করুন <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </Reveal>
        )}
      </Section>

      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: "ফ্রি এসইও অডিট টুল",
        inLanguage: "bn-BD",
        url: `${SITE.url}/bn/free-seo-audit`,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Any",
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        creator: { "@type": "Person", "@id": `${SITE.url}/#person`, name: SITE.fullName },
      }} />
      <JsonLd data={breadcrumbSchema(CRUMBS)} />
    </>
  );
}
